'use client';
import React, { useState } from 'react';
import QuizLobby from './QuizLobby';
import QuizPlayer from './QuizPlayer';
import QuizResults from './QuizResults';
import QuizStats from './QuizStats';

export type QuizMode = 'lobby' | 'playing' | 'results' | 'stats';

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  category: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  points: number;
  timeLimit: number;
}

export interface QuizAttempt {
  questionId: string;
  selectedIndex: number | null;
  isCorrect: boolean;
  timeTaken: number;
  pointsEarned: number;
}

export interface QuizSession {
  quizId: string;
  title: string;
  category: string;
  totalQuestions: number;
  attempts: QuizAttempt[];
  totalScore: number;
  totalTime: number;
  completedAt: string;
}

const QUIZ_BANK: Record<string, { title: string; category: string; emoji: string; description: string; questions: QuizQuestion[] }> = {
  'general-knowledge': {
    title: 'General Knowledge Trivia',
    category: 'Knowledge',
    emoji: '🧠',
    description: 'Test your knowledge across science, history, geography and more.',
    questions: [
      { id: 'gk-1', question: 'What is the capital city of Australia?', options: ['Sydney', 'Melbourne', 'Canberra', 'Brisbane'], correctIndex: 2, explanation: 'Canberra is the capital of Australia, chosen as a compromise between Sydney and Melbourne.', category: 'Geography', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'gk-2', question: 'Which planet is known as the Red Planet?', options: ['Venus', 'Mars', 'Jupiter', 'Saturn'], correctIndex: 1, explanation: 'Mars appears red due to iron oxide (rust) on its surface.', category: 'Science', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'gk-3', question: 'Who painted the Mona Lisa?', options: ['Michelangelo', 'Raphael', 'Leonardo da Vinci', 'Donatello'], correctIndex: 2, explanation: 'Leonardo da Vinci painted the Mona Lisa between 1503 and 1519.', category: 'Art', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'gk-4', question: 'What is the chemical symbol for gold?', options: ['Go', 'Gd', 'Au', 'Ag'], correctIndex: 2, explanation: 'Au comes from the Latin word "aurum" meaning gold.', category: 'Science', difficulty: 'Medium', points: 150, timeLimit: 20 },
      { id: 'gk-5', question: 'In which year did World War II end?', options: ['1943', '1944', '1945', '1946'], correctIndex: 2, explanation: 'World War II ended in 1945 with Germany surrendering in May and Japan in September.', category: 'History', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'gk-6', question: 'What is the largest ocean on Earth?', options: ['Atlantic', 'Indian', 'Arctic', 'Pacific'], correctIndex: 3, explanation: 'The Pacific Ocean is the largest, covering more than 30% of Earth\'s surface.', category: 'Geography', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'gk-7', question: 'Which element has the atomic number 1?', options: ['Helium', 'Hydrogen', 'Lithium', 'Carbon'], correctIndex: 1, explanation: 'Hydrogen is the lightest and most abundant element in the universe.', category: 'Science', difficulty: 'Medium', points: 150, timeLimit: 20 },
      { id: 'gk-8', question: 'How many sides does a hexagon have?', options: ['5', '6', '7', '8'], correctIndex: 1, explanation: 'A hexagon has 6 sides. "Hex" is Greek for six.', category: 'Mathematics', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'gk-9', question: 'Which country is home to the kangaroo?', options: ['New Zealand', 'South Africa', 'Australia', 'Brazil'], correctIndex: 2, explanation: 'Kangaroos are native to Australia and are one of its most iconic animals.', category: 'Nature', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'gk-10', question: 'What is the speed of light approximately?', options: ['200,000 km/s', '300,000 km/s', '400,000 km/s', '500,000 km/s'], correctIndex: 1, explanation: 'The speed of light in a vacuum is approximately 299,792 km/s, rounded to 300,000 km/s.', category: 'Science', difficulty: 'Medium', points: 150, timeLimit: 25 },
    ],
  },
  'tech-quiz': {
    title: 'Technology & Coding Quiz',
    category: 'Technology',
    emoji: '💻',
    description: 'Challenge your tech knowledge from programming to hardware.',
    questions: [
      { id: 'tech-1', question: 'What does HTML stand for?', options: ['Hyper Text Markup Language', 'High Tech Modern Language', 'Hyper Transfer Markup Language', 'Home Tool Markup Language'], correctIndex: 0, explanation: 'HTML stands for HyperText Markup Language, the standard language for web pages.', category: 'Web', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'tech-2', question: 'Which data structure uses LIFO (Last In, First Out)?', options: ['Queue', 'Stack', 'Array', 'Linked List'], correctIndex: 1, explanation: 'A Stack uses LIFO — the last element added is the first to be removed.', category: 'Programming', difficulty: 'Medium', points: 150, timeLimit: 25 },
      { id: 'tech-3', question: 'What does CSS stand for?', options: ['Computer Style Sheets', 'Cascading Style Sheets', 'Creative Style System', 'Coded Style Sheets'], correctIndex: 1, explanation: 'CSS stands for Cascading Style Sheets, used to style HTML documents.', category: 'Web', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'tech-4', question: 'Which company created the JavaScript programming language?', options: ['Microsoft', 'Google', 'Netscape', 'Apple'], correctIndex: 2, explanation: 'JavaScript was created by Brendan Eich at Netscape in 1995.', category: 'Programming', difficulty: 'Medium', points: 150, timeLimit: 25 },
      { id: 'tech-5', question: 'What is the time complexity of binary search?', options: ['O(n)', 'O(n²)', 'O(log n)', 'O(1)'], correctIndex: 2, explanation: 'Binary search has O(log n) complexity as it halves the search space each iteration.', category: 'Algorithms', difficulty: 'Hard', points: 200, timeLimit: 30 },
      { id: 'tech-6', question: 'What does API stand for?', options: ['Application Programming Interface', 'Automated Program Integration', 'Application Process Interface', 'Advanced Programming Index'], correctIndex: 0, explanation: 'API stands for Application Programming Interface — a way for programs to communicate.', category: 'Programming', difficulty: 'Easy', points: 100, timeLimit: 20 },
      { id: 'tech-7', question: 'Which of these is NOT a JavaScript framework?', options: ['React', 'Vue', 'Django', 'Angular'], correctIndex: 2, explanation: 'Django is a Python web framework, not a JavaScript framework.', category: 'Web', difficulty: 'Medium', points: 150, timeLimit: 25 },
      { id: 'tech-8', question: 'What does SQL stand for?', options: ['Structured Query Language', 'Simple Query Language', 'Standard Query Logic', 'Structured Question Language'], correctIndex: 0, explanation: 'SQL stands for Structured Query Language, used to manage relational databases.', category: 'Database', difficulty: 'Easy', points: 100, timeLimit: 20 },
    ],
  },
  'math-challenge': {
    title: 'Speed Mathematics',
    category: 'Mathematics',
    emoji: '🔢',
    description: 'Race against the clock with mental math challenges.',
    questions: [
      { id: 'math-1', question: 'What is 15 × 8?', options: ['110', '120', '125', '130'], correctIndex: 1, explanation: '15 × 8 = 120. Think of it as 15 × 8 = (10 × 8) + (5 × 8) = 80 + 40 = 120.', category: 'Arithmetic', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'math-2', question: 'What is the square root of 144?', options: ['11', '12', '13', '14'], correctIndex: 1, explanation: '√144 = 12, because 12 × 12 = 144.', category: 'Algebra', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'math-3', question: 'If x + 7 = 15, what is x?', options: ['6', '7', '8', '9'], correctIndex: 2, explanation: 'x = 15 - 7 = 8.', category: 'Algebra', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'math-4', question: 'What is 25% of 200?', options: ['40', '45', '50', '55'], correctIndex: 2, explanation: '25% of 200 = 200 × 0.25 = 50.', category: 'Percentages', difficulty: 'Easy', points: 100, timeLimit: 15 },
      { id: 'math-5', question: 'What is 7³ (7 cubed)?', options: ['343', '349', '357', '363'], correctIndex: 0, explanation: '7³ = 7 × 7 × 7 = 49 × 7 = 343.', category: 'Powers', difficulty: 'Medium', points: 150, timeLimit: 20 },
      { id: 'math-6', question: 'What is the area of a circle with radius 5? (Use π ≈ 3.14)', options: ['78.5', '31.4', '62.8', '15.7'], correctIndex: 0, explanation: 'Area = π × r² = 3.14 × 25 = 78.5.', category: 'Geometry', difficulty: 'Medium', points: 150, timeLimit: 25 },
      { id: 'math-7', question: 'What is 1/3 + 1/6?', options: ['1/2', '2/9', '1/3', '2/6'], correctIndex: 0, explanation: '1/3 + 1/6 = 2/6 + 1/6 = 3/6 = 1/2.', category: 'Fractions', difficulty: 'Medium', points: 150, timeLimit: 20 },
      { id: 'math-8', question: 'What is the next prime number after 13?', options: ['14', '15', '16', '17'], correctIndex: 3, explanation: '17 is the next prime after 13. 14, 15, 16 are all composite numbers.', category: 'Number Theory', difficulty: 'Medium', points: 150, timeLimit: 20 },
    ],
  },
};

const HISTORY_STORAGE_KEY = 'brainbasket_quiz_history';

function loadHistory(): QuizSession[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(sessions: QuizSession[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(sessions.slice(0, 20)));
  } catch {
    // ignore
  }
}

export default function QuizArenaContent() {
  const [mode, setMode] = useState<QuizMode>('lobby');
  const [selectedQuizId, setSelectedQuizId] = useState<string>('general-knowledge');
  const [currentSession, setCurrentSession] = useState<QuizSession | null>(null);
  const [history, setHistory] = useState<QuizSession[]>(() => loadHistory());

  const handleStartQuiz = (quizId: string) => {
    setSelectedQuizId(quizId);
    setMode('playing');
  };

  const handleQuizComplete = (session: QuizSession) => {
    setCurrentSession(session);
    const updated = [session, ...history];
    setHistory(updated);
    saveHistory(updated);
    setMode('results');
  };

  const handleViewStats = () => setMode('stats');
  const handlePlayAgain = () => setMode('playing');
  const handleBackToLobby = () => setMode('lobby');

  const quizData = QUIZ_BANK[selectedQuizId];

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {mode === 'lobby' && (
        <QuizLobby
          quizBank={QUIZ_BANK}
          history={history}
          onStart={handleStartQuiz}
          onViewStats={handleViewStats}
        />
      )}
      {mode === 'playing' && quizData && (
        <QuizPlayer
          quizId={selectedQuizId}
          title={quizData.title}
          category={quizData.category}
          questions={quizData.questions}
          onComplete={handleQuizComplete}
          onExit={handleBackToLobby}
        />
      )}
      {mode === 'results' && currentSession && (
        <QuizResults
          session={currentSession}
          quizData={quizData}
          onPlayAgain={handlePlayAgain}
          onViewStats={handleViewStats}
          onBackToLobby={handleBackToLobby}
        />
      )}
      {mode === 'stats' && (
        <QuizStats
          history={history}
          onBack={handleBackToLobby}
        />
      )}
    </div>
  );
}
